/**
 * MissionEngine: runs the clinically generated plan target by target, evaluates movement samples
 * and produces MovementAttempt records. Pure logic (no React, no DOM) so it can be ported to Unity C#.
 */
const TARGET_TIMEOUT_MS = 25000;
const OVER_LIMIT_TOLERANCE = 1.08;

export class MissionEngine {
  constructor(plan, movement) {
    this.plan = plan;
    this.mv = movement;
    this.r = plan.hit_radius;
    this.maxTries = plan.max_tries_per_target;
    this.mi = 0;
    this.ti = 0;
  }

  get mission() { return this.plan.missions[this.mi]; }
  get target() { return this.mission?.targets[this.ti]; }
  get totalTargets() { return this.plan.missions.reduce((n, m) => n + m.targets.length, 0); }

  beginTarget(now = performance.now()) {
    const t = this.target;
    this.s = { shownAt: now, firstTouch: null, tries: 0, holds: {}, done: {}, dragging: false,
      pos: t.start ? { ...t.start } : null, lastMove: null, aimSince: null, violations: 0, peak: 0, lastWarn: 0 };
  }

  next() {
    this.ti += 1;
    if (this.ti >= this.mission.targets.length) { this.mi += 1; this.ti = 0; return { missionDone: true, levelDone: this.mi >= this.plan.missions.length }; }
    return { missionDone: false, levelDone: false };
  }

  nearest(p, aspect) {
    return this.target.positions
      .map((pos, i) => ({ i, pos, d: this.mv.distance(p, pos, aspect) }))
      .sort((a, b) => a.d - b.d)[0];
  }

  attempt(success, { achieved, accuracy, now }) {
    const t = this.target, m = this.mission, s = this.s;
    const first = s.firstTouch ?? now;
    return {
      mission_key: m.key, target_index: t.index, movement: m.movement, metric: m.metric, unit: m.unit, side: t.side,
      target_position: t.positions, expected_amplitude: t.expected_amplitude, safe_limit: t.safe_limit,
      achieved_amplitude: achieved, over_limit: achieved > t.safe_limit * OVER_LIMIT_TOLERANCE,
      accuracy: Math.round(Math.max(0, Math.min(100, accuracy)) * 10) / 10,
      reaction_time_ms: Math.round(first - s.shownAt), movement_time_ms: Math.round(now - first),
      success, repetition: t.index + 1, tries: Math.max(1, s.tries), difficulty: t.difficulty,
      interaction_variant: m.projectile && m.projectile !== "none" ? m.projectile : m.interaction,
    };
  }

  miss(p, aspect, now, achieved) {
    const s = this.s;
    s.tries += 1;
    if (s.tries >= this.maxTries) {
      const d = this.nearest(p, aspect).d;
      return { type: "fail", attempt: this.attempt(false, { achieved, accuracy: 55 - (d / this.r - 1) * 25, now }) };
    }
    return { type: "miss" };
  }

  /** Called on every animation frame for time-based rules (hold, timeout). */
  tick(now) {
    const s = this.s, m = this.mission, t = this.target;
    if (!s || !t) return null;
    if (m.interaction === "hold") {
      Object.entries(s.holds).forEach(([i, h]) => { if (now - h.since >= m.hold_ms) s.done[i] = h.amp; });
      if (Object.keys(s.done).length === t.positions.length) {
        const achieved = Math.min(...Object.values(s.done));
        return { type: "hit", attempt: this.attempt(true, { achieved, accuracy: 100 - Math.max(0, s.tries) * 8, now }) };
      }
    }
    if (now - s.shownAt > TARGET_TIMEOUT_MS) {
      return { type: "timeout", attempt: this.attempt(false, { achieved: Math.round(s.peak * 10) / 10, accuracy: 0, now }) };
    }
    return null;
  }

  handle(sample) {
    const m = this.mission, t = this.target, s = this.s;
    if (!t || !s) return null;
    const now = sample.t, aspect = sample.aspect || 1.8, p = { x: sample.x, y: sample.y };
    const { amplitude } = this.mv.toMovement(sample, m.metric);
    if (sample.phase === "start" && s.firstTouch === null) s.firstTouch = now;
    s.peak = Math.max(s.peak, amplitude);
    const near = this.nearest(p, aspect);
    const accuracyOf = (d) => 100 - (d / this.r) * 35;

    if (m.interaction === "tap") {
      const throwing = m.projectile && m.projectile !== "none";
      if (sample.phase !== "end") {
        if (throwing) s.aimSince = near.d <= this.r ? (s.aimSince ?? now) : null;
        return { type: "aim", p };
      }
      const steady = !throwing || (s.aimSince != null && now - s.aimSince >= m.aim_hold_ms);
      s.aimSince = null;
      if (near.d <= this.r) {
        if (!steady) return { type: "too_quick", p };
        return { type: "hit", p, attempt: this.attempt(true, { achieved: amplitude, accuracy: accuracyOf(near.d), now }) };
      }
      return { ...this.miss(p, aspect, now, amplitude), p };
    }

    if (m.interaction === "drag") {
      if (sample.phase === "start") {
        s.dragging = this.mv.distance(p, s.pos, aspect) <= this.r * 1.4;
        return s.dragging ? { type: "grab", p } : { type: "hint", text: "grab" };
      }
      if (!s.dragging) return null;
      if (sample.phase === "move") { s.pos = p; return { type: "drag", p }; }
      s.dragging = false;
      if (near.d <= this.r) return { type: "hit", attempt: this.attempt(true, { achieved: amplitude, accuracy: accuracyOf(near.d) - s.tries * 5, now }) };
      s.pos = { ...t.start };
      return { ...this.miss(p, aspect, now, amplitude), reset: true };
    }

    if (m.interaction === "hold") {
      const key = String(near.i);
      if (sample.phase === "end") {
        Object.keys(s.holds).forEach((k) => { if (s.holds[k].id === sample.id && !s.done[k]) { delete s.holds[k]; s.tries += 1; } });
        return { type: "release" };
      }
      if (near.d <= this.r * 1.2) {
        if (!s.holds[key]) s.holds[key] = { id: sample.id, since: now, amp: amplitude };
        return { type: "holding", index: near.i };
      }
      Object.keys(s.holds).forEach((k) => { if (s.holds[k].id === sample.id && !s.done[k]) delete s.holds[k]; });
      return null;
    }

    if (m.interaction === "slow_drag") {
      if (sample.phase === "start") {
        s.dragging = this.mv.distance(p, s.pos, aspect) <= this.r * 1.6;
        s.lastMove = { p, t: now };
        return s.dragging ? { type: "grab", p } : { type: "hint", text: "grab" };
      }
      if (!s.dragging) return null;
      if (sample.phase === "end") {
        s.dragging = false;
        s.tries += 1;
        return { type: "release" };
      }
      const dt = (now - s.lastMove.t) / 1000;
      const speed = dt > 0 ? this.mv.distance(p, s.lastMove.p, aspect) / dt : 0;
      s.lastMove = { p, t: now };
      s.pos = p;
      let warn = false;
      if (speed > m.max_speed && dt > 0.01 && now - s.lastWarn > 700) { s.violations += 1; s.lastWarn = now; warn = true; }
      if (near.d <= this.r) {
        s.dragging = false;
        return { type: "hit", attempt: this.attempt(true, { achieved: amplitude, accuracy: accuracyOf(near.d) - s.violations * 8, now }) };
      }
      return { type: warn ? "too_fast" : "drag", p };
    }
    return null;
  }
}

export const starsFor = (attempts) => {
  if (!attempts.length) return 0;
  const success = attempts.filter((a) => a.success).length / attempts.length;
  const acc = attempts.reduce((n, a) => n + a.accuracy, 0) / attempts.length;
  if (success >= 0.8 && acc >= 75) return 3;
  if (success >= 0.5) return 2;
  return 1;
};
